import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import usePbNoShowSignalsApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';

const Container = styled.div``;

export default function PbNoShowSignalsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('PB') : false;

  const [queryParams, setQueryParams] = useState({ pageSize: 25, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [confirm, setConfirm] = useState(null);

  const { fetching, data, pagination, columns: baseColumns, resolve, scan } = usePbNoShowSignalsApi(queryParams);

  const handleDataStateChange = useCallback((act, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const buildActions = useCallback((row) => {
    if (!row || !writable) return [];
    if (row.resolvedAt) return [];
    return [{
      type: 'edit',
      label: t('preboardingSignal.action.resolve'),
      onClick: () => setConfirm({
        message: t('preboardingSignal.confirm.resolve'),
        onYes: () => resolve(row.id, t('preboardingSignal.defaultResolutionNote')),
      }),
    }];
  }, [t, writable, resolve]);

  const columns = useMemo(() => {
    if (!writable) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, writable]);

  return (
    <Container>
      {useMemo(() => (
        <DataTable
          title={t('preboardingSignal.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          customToolbar={writable ? () => (
            <span style={{ marginLeft: 8 }}>
              <button
                type="button"
                onClick={() => setConfirm({
                  message: t('preboardingSignal.confirm.scan'),
                  onYes: () => scan(),
                })}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
              >
                {t('preboardingSignal.action.scan')}
              </button>
            </span>
          ) : undefined}
        />
      ), [t, fetching, columns, data, pagination, filterList, handleFilterConfirm, handleDataStateChange, writable, scan])}
      <ConfirmDialog
        isOpen={!!confirm}
        message={confirm?.message}
        onConfirm={async () => { await confirm.onYes(); setConfirm(null); }}
        onCancel={() => setConfirm(null)}
      />
    </Container>
  );
}
